import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/icon/icon";
import Table from "../../../components/table/table";
import { ITEMS_PER_PAGE_OPTIONS } from "../../../constants/pagination";
import { Product } from "../../../types";
import { ProductTableProps } from "../types/products-types";
import {
  formatMileage,
  getProductDisplayName,
  getProductSpecsText,
  getRentalPriceText,
  hasActiveDiscount,
} from "../utils/products-utils";

export const ProductTable: React.FC<ProductTableProps> = ({
  products,
  isLoading,
  currentPage,
  itemsPerPage,
  totalPages,
  onEdit,
  onDelete,
  onDuplicate,
  onReorder,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: "Номер лота",
      dataIndex: "licensePlate" as keyof Product,
      key: "licensePlate",
      render: (licensePlate: string) => (
        <div className="product-license-plate">LOT {licensePlate}</div>
      ),
    },
    {
      title: "Автомобиль",
      key: "vehicle",
      render: (_: any, record: Product) => (
        <div className="product-vehicle">
          <div className="product-info">
            <div className="product-name">{getProductDisplayName(record)}</div>
            <div className="product-specs-text">
              {record.year || 0} • {formatMileage(record.mileage || 0)} км
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Характеристики",
      key: "specs",
      render: (_: any, record: Product) => (
        <div className="product-specs">
          <div className="spec-item">{getProductSpecsText(record)}</div>
        </div>
      ),
    },
    {
      title: "Аренда",
      key: "rental",
      render: (_: any, record: Product) => {
        const hasDiscount = hasActiveDiscount(record);

        return (
          <div className="product-rental">
            <div
              className={`rental-price ${
                hasDiscount ? "rental-price-discount" : ""
              }`}
            >
              {getRentalPriceText(record)}
              {hasDiscount && <span className="discount-badge">Скидка</span>}
            </div>
          </div>
        );
      },
    },
    {
      title: "Статус",
      key: "status",
      render: (_: any, record: Product) => {
        const status = record.status || "ACTIVE";
        const getStatusClass = (status: string) => {
          switch (status.toUpperCase()) {
            case "ACTIVE":
              return "status-text-active";
            case "RENTING":
            case "RENTED":
              return "status-text-renting";
            case "ARCHIVED":
              return "status-text-archived";
            default:
              return "status-text-default";
          }
        };

        const getStatusLabel = (status: string) => {
          switch (status.toUpperCase()) {
            case "ACTIVE":
              return "Активен";
            case "RENTING":
            case "RENTED":
              return "В аренде";
            case "ARCHIVED":
              return "Архивный";
            default:
              return status;
          }
        };

        return (
          <div className="product-status">
            <div className="status-badges">
              <span className={`status-badge ${getStatusClass(status)}`}>
                {getStatusLabel(status)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Действия",
      key: "action",
      dataIndex: "id" as keyof Product,
      render: (_: any, record: Product) => (
        <div className="products__actions">
          <button
            onClick={() => navigate(`/products/${record.id}`)}
            className="products__action-btn"
            title="Просмотр деталей"
          >
            <Icon name="eye" size="small" />
          </button>
          <button
            onClick={() => onEdit(record)}
            className="products__action-btn"
            title="Редактировать"
          >
            <Icon name="pencil" size="small" />
          </button>
          <button
            onClick={() => onDuplicate(record)}
            className="products__action-btn"
            title="Дублировать"
          >
            <Icon name="dublicate" size="small" />
          </button>
          <button
            onClick={() => onDelete(record)}
            className="products__action-btn"
            title="Удалить"
          >
            <Icon name="delete" size="small" />
          </button>
          <button
            onClick={() => onReorder(record, record.order)}
            className="products__action-btn"
            title="Изменить порядок"
          >
            <Icon name="up-down" size="small" />
          </button>
        </div>
      ),
    },
  ];

  const handleItemsPerPageChange = (value: string): void => {
    onItemsPerPageChange(parseInt(value));
  };

  return (
    <div className="products__table-container">
      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={isLoading}
        emptyMessage="Продукты не найдены"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        rowClassName={(record: Product) =>
          hasActiveDiscount(record) ? "table-row-discount" : ""
        }
      />

      <div className="products__pagination">
        <div className="products__pagination-info">
          Показано{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, products.length)} -{" "}
          {Math.min(currentPage * itemsPerPage, products.length)} из{" "}
          {products.length}
        </div>

        <div className="products__pagination-controls">
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
            className="products__page-size-select"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} на странице
              </option>
            ))}
          </select>

          <div className="products__page-buttons">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="products__page-btn"
            >
              <Icon name="left" size="small" />
            </button>

            <span className="products__page-info">
              {currentPage} из {totalPages}
            </span>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="products__page-btn"
            >
              <Icon name="right" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
